import {
  playerDisplayName,
  rosterLookup,
} from "./liveTrainingPlayerContext";

/** Active (green) vs Sparring — matches backend / iOS role strings. */
export function isActiveExerciseRole(role) {
  if (role == null || role === "") return false;
  const k = String(role).toLowerCase();
  return (
    k === "active" ||
    k.includes("actif") ||
    k.includes("activo") ||
    k.includes("aktiv")
  );
}

export function sortedExerciseSequences(exercise) {
  return [...(exercise?.sequences || [])].sort(
    (a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0)
  );
}

function localizedName(obj, prefix, lang = "en") {
  if (!obj) return "";
  const base = String(lang || "en").split("-")[0];
  const loc = base === "sp" ? "es" : base;
  return (
    String(obj[`${prefix}_${loc}`] || "").trim() ||
    String(obj[`${prefix}_${base}`] || "").trim() ||
    String(obj[`${prefix}_en`] || "").trim() ||
    String(obj[`${prefix}_fr`] || "").trim() ||
    String(obj[`${prefix}_de`] || "").trim() ||
    String(obj[`${prefix}_es`] || "").trim() ||
    ""
  );
}

/** Display name for a sequence row (subtype, competency block, or exercise type). */
export function getExerciseSequenceName(seq, exercise = null, lang = "en") {
  if (!seq) return "";
  const loc = String(lang || "en").split("-")[0];
  return (
    localizedName(seq.competency_block, "cb_name", loc) ||
    localizedName(seq.blocks_compentency, "cb_name", loc) ||
    localizedName(seq, "sub_type_name", loc) ||
    localizedName(seq.sub_type_ex, "sub_type_name", loc) ||
    String(seq.sub_type_name || "").trim() ||
    localizedName(seq, "ex_name", loc) ||
    localizedName(seq.tbl_exercise, "ex_name", loc) ||
    localizedName(seq.exercise, "ex_name", loc) ||
    localizedName(exercise?.type_ex, "type_name", loc) ||
    ""
  );
}

export function formatSequenceOrderName(seq, exercise, lang, fallbackIndex) {
  if (!seq) return "";
  const order = seq.sequence_order ?? fallbackIndex;
  const name = getExerciseSequenceName(seq, exercise, lang);
  if (order == null || order === "") return name;
  return name ? `${order}. ${name}` : String(order);
}

/** Current + next sequence for the coach Live Training bar. */
export function resolveCoachSequenceNav(exercise, lang = "en") {
  const seqs = sortedExerciseSequences(exercise);
  if (!seqs.length) {
    return { current: null, next: null, idx: -1 };
  }
  const cs = exercise?.currentSequence;
  let idx = 0;
  if (cs && typeof cs === "object") {
    const byTes = seqs.findIndex(
      (s) => cs.tes_id != null && String(s.tes_id) === String(cs.tes_id)
    );
    const byOrder = seqs.findIndex(
      (s) =>
        cs.sequence_order != null && s.sequence_order === cs.sequence_order
    );
    idx = byTes >= 0 ? byTes : byOrder >= 0 ? byOrder : 0;
  } else if (typeof cs === "number") {
    const byOrder = seqs.findIndex((s) => s.sequence_order === cs);
    idx =
      byOrder >= 0 ? byOrder : Math.max(0, Math.min(seqs.length - 1, cs));
  }
  const current = seqs[idx] || null;
  const next = idx + 1 < seqs.length ? seqs[idx + 1] : null;
  return {
    idx,
    current: current
      ? {
          order: current.sequence_order ?? idx + 1,
          name: getExerciseSequenceName(current, exercise, lang),
          caption: formatSequenceOrderName(current, exercise, lang, idx + 1),
        }
      : null,
    next: next
      ? {
          order: next.sequence_order ?? idx + 2,
          name: getExerciseSequenceName(next, exercise, lang),
          caption: formatSequenceOrderName(next, exercise, lang, idx + 2),
        }
      : null,
  };
}

/**
 * Sequence to preview above coach session controls.
 * Idle / between blocks → the block about to start. Running → the following block.
 */
export function resolveCoachNextSequenceInfo(
  exercise,
  {
    lang = "en",
    sequenceInProgress = false,
    awaitingManualSequenceStart = false,
    awaitingAutoSequenceStart = false,
  } = {}
) {
  const nav = resolveCoachSequenceNav(exercise, lang);
  const awaiting = !!(
    awaitingManualSequenceStart || awaitingAutoSequenceStart
  );
  if (awaiting) {
    const upcoming = resolveUpcomingSequenceOrder(exercise);
    const completed = resolveCompletedSequenceOrder(exercise, {
      awaitingManualSequenceStart,
      awaitingAutoSequenceStart,
    });
    if (upcoming != null && completed != null && upcoming === completed) {
      return nav.next;
    }
    return nav.current;
  }
  if (!sequenceInProgress) return nav.current;
  return nav.next;
}

/**
 * Title under the Live Training coach timer for warmup sequences.
 * Running sequence → its name. Otherwise → the next sequence about to start.
 */
export function resolveWarmupCoachHeroTitle(
  exercise,
  {
    lang = "en",
    sequenceInProgress = false,
    awaitingManualSequenceStart = false,
    awaitingAutoSequenceStart = false,
  } = {}
) {
  const nav = resolveCoachSequenceNav(exercise, lang);
  const awaiting = !!(
    awaitingManualSequenceStart || awaitingAutoSequenceStart
  );
  let target = nav.current;
  if (!sequenceInProgress && awaiting && nav.next) {
    const upcoming = resolveUpcomingSequenceOrder(exercise);
    const completed = resolveCompletedSequenceOrder(exercise, {
      awaitingManualSequenceStart,
      awaitingAutoSequenceStart,
    });
    // Payload still on the finished block → show the following sequence.
    if (upcoming != null && completed != null && upcoming === completed) {
      target = nav.next;
    }
  }
  if (!target) return { order: null, name: "" };
  return { order: target.order ?? null, name: target.name || "" };
}

/** One-based sequence index from `exercise.currentSequence` (object or number). */
export function resolveUpcomingSequenceOrder(exercise) {
  const cs = exercise?.currentSequence;
  if (cs == null) return null;
  if (typeof cs === "number") return cs;
  if (typeof cs === "object") return cs.sequence_order ?? null;
  return null;
}

/**
 * Sequence that just finished when waiting between blocks (manual or auto pause).
 * During a gap, `currentSequence` points at the upcoming block.
 */
export function resolveCompletedSequenceOrder(
  exercise,
  {
    awaitingManualSequenceStart,
    awaitingAutoSequenceStart,
    lastSequenceCompleted,
  } = {}
) {
  const upcoming = resolveUpcomingSequenceOrder(exercise);
  if (upcoming == null) return null;
  const awaitingGap = !!(
    awaitingManualSequenceStart || awaitingAutoSequenceStart
  );
  if (awaitingGap && upcoming > 1) return upcoming - 1;
  if (lastSequenceCompleted || awaitingGap) return upcoming;
  return upcoming;
}

/** Pick the sequence row for a given order (and table when partitioned). */
export function pickSequenceRowForOrder(exercise, order, tableNumber) {
  const seqs = sortedExerciseSequences(exercise);
  if (!seqs.length || order == null) return null;
  const matches = seqs.filter((s) => s.sequence_order === order);
  if (!matches.length) return null;
  const tableStr =
    tableNumber != null && tableNumber !== "" ? String(tableNumber) : null;
  if (!tableStr) return matches[0];
  return (
    matches.find(
      (s) =>
        s.table_number != null && String(s.table_number) === tableStr
    ) ?? matches[0]
  );
}

export function viewerSlotInSequence(seq, viewerPlayerId, viewerUserId) {
  if (!seq) return null;
  const pid = viewerPlayerId != null ? String(viewerPlayerId) : null;
  const uid = viewerUserId != null ? String(viewerUserId) : null;
  const matchId = (slotId) => {
    if (slotId == null) return false;
    const s = String(slotId);
    return (pid && s === pid) || (uid && s === uid);
  };
  if (matchId(seq.player_a_id)) {
    return { side: "A", role: seq.player_a_role || null, playerId: seq.player_a_id };
  }
  if (matchId(seq.player_b_id)) {
    return { side: "B", role: seq.player_b_role || null, playerId: seq.player_b_id };
  }
  if (matchId(seq.player_c_id)) {
    return { side: "C", role: seq.player_c_role || null, playerId: seq.player_c_id };
  }
  if (matchId(seq.player_d_id)) {
    return { side: "D", role: seq.player_d_role || null, playerId: seq.player_d_id };
  }
  return null;
}

export function viewerWasActiveInSequence(seq, viewerPlayerId, viewerUserId) {
  const slot = viewerSlotInSequence(seq, viewerPlayerId, viewerUserId);
  return Boolean(slot && isActiveExerciseRole(slot.role));
}

export function activePlayerIdInSequence(seq) {
  if (!seq) return null;
  const slots = [
    { id: seq.player_a_id, role: seq.player_a_role },
    { id: seq.player_b_id, role: seq.player_b_role },
    { id: seq.player_c_id, role: seq.player_c_role },
    { id: seq.player_d_id, role: seq.player_d_role },
  ];
  for (const s of slots) {
    if (s.id != null && isActiveExerciseRole(s.role)) return s.id;
  }
  return null;
}

export function resolvePlayerNameById(roster, playerId, user) {
  const entry = rosterLookup(roster, playerId);
  return (
    playerDisplayName(entry) ||
    playerDisplayName(user) ||
    (playerId ? String(playerId).slice(-6) : null)
  );
}

/** Resolve display name of the active-role player for the completed sequence block. */
export function resolveActivePlayerNameForSequence(
  exercise,
  seq,
  roster,
  viewerPlayerId,
  viewerUserId,
  user
) {
  if (!seq) return null;
  const viewerSlot = viewerSlotInSequence(seq, viewerPlayerId, viewerUserId);
  if (viewerSlot && isActiveExerciseRole(viewerSlot.role)) {
    return resolvePlayerNameById(roster, viewerSlot.playerId, user);
  }
  const activeId = activePlayerIdInSequence(seq);
  if (activeId != null) {
    return resolvePlayerNameById(roster, activeId, user);
  }
  return resolvePlayerNameById(roster, viewerPlayerId, user);
}

export function buildSequenceAutoevalKey(teId, seq) {
  const tesId = seq?.tes_id;
  if (tesId != null && tesId !== "") return String(tesId);
  const order = seq?.sequence_order;
  return order != null ? `${teId}-seq-${order}` : String(teId);
}

export function buildSequenceAutoevalSubtitle({ t, playerName, number, label }) {
  const parts = [];
  if (playerName) parts.push(playerName);
  const seqPart = `${t("liveTrainingSequence")} ${number ?? "?"}`;
  parts.push(label ? `${seqPart} · ${label}` : seqPart);
  return parts.join(" · ");
}

export function resolveSequenceAutoevalContext({
  exercise,
  teId,
  roster,
  viewerPlayerId,
  viewerUserId,
  viewerTableNumber,
  user,
  awaitingManualSequenceStart,
  awaitingAutoSequenceStart,
  lastSequenceCompleted,
  t,
}) {
  if (!exercise || !teId) return null;
  const completedOrder = resolveCompletedSequenceOrder(exercise, {
    awaitingManualSequenceStart,
    awaitingAutoSequenceStart,
    lastSequenceCompleted,
  });
  if (completedOrder == null) return null;

  const seq = pickSequenceRowForOrder(
    exercise,
    completedOrder,
    viewerTableNumber
  );
  if (!seq) return null;
  if (!viewerWasActiveInSequence(seq, viewerPlayerId, viewerUserId)) {
    return null;
  }

  const label =
    seq.sub_type_name_en || seq.sub_type_name_fr || seq.sub_type_name || null;
  const playerName = resolveActivePlayerNameForSequence(
    exercise,
    seq,
    roster,
    viewerPlayerId,
    viewerUserId,
    user
  );

  return {
    key: buildSequenceAutoevalKey(teId, seq),
    number: completedOrder,
    label,
    playerName,
    subtitle: buildSequenceAutoevalSubtitle({
      t,
      playerName,
      number: completedOrder,
      label,
    }),
  };
}
